import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "@/App";
import MarketingNav from "@/components/MarketingNav";
import MarketingFooter from "@/components/MarketingFooter";
import { ArrowRight, Check } from "lucide-react";
import {
  TRUST_TOOLS,
  VERDICT_CONTENTS,
  HOW_STEPS,
  REVIEW_AREAS,
  REVIEW_PRINCIPLES,
} from "@/data/landingCurated";
import { REVIEW_TIERS, REVIEW_PRICE_FROM } from "@/lib/reviewPricing";
import { track } from "@/lib/telemetry";
import { isFlagOn } from "@/lib/flags";

/**
 * LandingPage: concierge mode (2026-10-09).
 *
 * Xpert launches as a concierge service. Every review is done by a named
 * human, today the founder, so the page sends visitors to one doorway
 * (`/get-verified`) and says plainly who reviews the work. Browsing the
 * directory stays available from the footer, not from the hero.
 *
 * Sections (top to bottom):
 *   1. Hero: eyebrow, H1 with one italic green phrase, lead, primary CTA to
 *      /get-verified, "How it works" anchor, "what you get back" aside
 *   2. How it works (#how): three steps
 *   3. Who reviews your work (#who): the founder, what he reviews, how
 *   4. Pricing (#pricing): the three list tiers from lib/reviewPricing
 *   5. Badge wall: live public badges only, graceful empty state
 *   6. Final CTA
 *
 * Removed on purpose, not to be restored without real data: the "earlier
 * today" verified stack, curated badge tiles, featured-expert cards (live
 * data today is the founder plus persona rows that take no async reviews),
 * the nine-domain use-case grid, invented testimonials, and the old tier
 * cards with stale prices.
 *
 * Brand rules: one italic green phrase in the H1, canonical eyebrow,
 * rounded-md CTAs, no third brand color, no invented numbers.
 */

// Wraps track() with the discovery_v2 flag check so disabling the flag
// silences emission without removing call sites.
function trackIfEnabled(name, props) {
  if (!isFlagOn("discovery_v2")) return;
  track(name, props);
}

export default function LandingPage() {
  const navigate = useNavigate();
  return (
    <div className="min-h-screen bg-canvas text-ink font-body">
      <MarketingNav />
      <Hero navigate={navigate} />
      <HowItWorks />
      <WhoReviews navigate={navigate} />
      <Pricing navigate={navigate} />
      <BadgeWall />
      <FinalCTA navigate={navigate} />
      <MarketingFooter />
    </div>
  );
}

// ---------------------------------------------------------------------------
// 1. Hero
// ---------------------------------------------------------------------------

function Hero({ navigate }) {
  return (
    <section className="relative overflow-hidden">
      {/* Soft green glow behind the hero: single 1-stop, no rainbow. */}
      <div
        aria-hidden="true"
        className="absolute -top-32 left-1/2 -translate-x-1/2 w-[900px] h-[480px] rounded-full
                   bg-brand-green/10 blur-3xl pointer-events-none"
      />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-12 sm:pt-20 pb-20 sm:pb-28 relative">
        <div className="grid lg:grid-cols-[1.1fr,1fr] gap-12 lg:gap-16 items-start">
          <div>
            {/* Canonical eyebrow line (CLAUDE.md §5) */}
            <p className="text-xs font-bold uppercase tracking-[0.18em] text-brand-green">
              The human review layer for AI-built work
            </p>

            {/* Three-line H1 with a single italic green accent */}
            <h1 className="mt-5 font-display text-[40px] sm:text-[56px] lg:text-[64px]
                            leading-[1.04] tracking-tight text-ink">
              <span className="block">Every AI tool forgot to add</span>
              <span className="block italic text-brand-green">a review button.</span>
              <span className="block">We built it.</span>
            </h1>

            <p className="mt-6 text-lg sm:text-xl text-slateline-600 leading-relaxed max-w-xl">
              Send the <strong className="text-ink font-semibold">code</strong>,{" "}
              <strong className="text-ink font-semibold">spec</strong> or{" "}
              <strong className="text-ink font-semibold">strategy doc</strong> your AI
              tools produced. A named human reviews it and sends back a written
              verdict in 24 hours, 48 hours or a week.
            </p>

            <div className="mt-7 flex flex-wrap items-center gap-3">
              <button
                onClick={() => {
                  trackIfEnabled("landing_hero_cta_clicked", { cta: "primary", to: "/get-verified" });
                  navigate("/get-verified");
                }}
                className="inline-flex items-center gap-2 h-12 px-6 rounded-md
                           bg-brand-green hover:bg-brand-greenHover
                           text-white font-semibold text-sm tracking-wide shadow-sm transition-colors"
              >
                Get your AI-built work reviewed <ArrowRight className="w-4 h-4" />
              </button>
              <a
                href="#how"
                onClick={() => trackIfEnabled("landing_hero_cta_clicked", { cta: "secondary", to: "#how" })}
                className="inline-flex items-center gap-2 h-12 px-6 rounded-md
                           bg-white border border-slateline-300 hover:border-ink/60
                           text-ink font-semibold text-sm tracking-wide transition-colors"
              >
                How it works
              </a>
            </div>

            {/* Microtrust: which AI tools' output we accept */}
            <div className="mt-9 flex flex-wrap items-center gap-x-6 gap-y-2">
              <span className="text-[11px] font-bold uppercase tracking-[0.14em] text-slateline-500">
                Works with output from
              </span>
              {TRUST_TOOLS.map((t) => (
                <span key={t} className="text-sm font-display text-slateline-600 tracking-tight">
                  {t}
                </span>
              ))}
            </div>
          </div>

          <VerdictContents />
        </div>
      </div>
    </section>
  );
}

/** Hero aside: what a review returns. A description, not a sample verdict. */
function VerdictContents() {
  return (
    <aside
      aria-label="What you get back"
      className="bg-white border border-slateline-200 rounded-xl p-6 sm:p-7 shadow-sm lg:mt-8"
    >
      <div className="flex items-center gap-3 pb-4 border-b border-slateline-100">
        <BrandCheck />
        <span className="text-[11px] font-bold uppercase tracking-[0.14em] text-slateline-500">
          What you get back
        </span>
      </div>
      <ul className="mt-4 space-y-3">
        {VERDICT_CONTENTS.map((line) => (
          <li key={line} className="flex items-start gap-2.5 text-sm text-ink leading-snug">
            <Check className="w-4 h-4 text-brand-green flex-shrink-0 mt-0.5" />
            <span>{line}</span>
          </li>
        ))}
      </ul>
      <p className="mt-5 pt-4 border-t border-slateline-100 text-sm text-slateline-600">
        24 hours, 48 hours or one week.{" "}
        <strong className="text-ink font-semibold">From ${REVIEW_PRICE_FROM}.</strong>
      </p>
    </aside>
  );
}

// The brand mark: white check on a green rounded square. Never recolor.
function BrandCheck({ size = 32 }) {
  return (
    <span
      className="flex-shrink-0 inline-flex items-center justify-center rounded-md bg-brand-green"
      style={{ width: size, height: size }}
    >
      <svg width={size / 2} height={size / 2} viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <path d="M 5 12.5 L 10 17.5 L 19 7" stroke="#FFFFFF" strokeWidth="3"
              strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    </span>
  );
}

// ---------------------------------------------------------------------------
// 2. How it works
// ---------------------------------------------------------------------------

function HowItWorks() {
  return (
    <section id="how" className="py-20 sm:py-28 scroll-mt-16">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <SectionHead
          eyebrow="How it works"
          h2={<>Three steps.<br />One named human.</>}
          sub={`No retainer. No intake call. No "we'll get back to you in five to seven business days."`}
        />
        <div className="grid md:grid-cols-3 gap-5 sm:gap-8 mt-12">
          {HOW_STEPS.map((s) => (
            <article key={s.n} className="bg-white border border-slateline-200 rounded-xl p-6 sm:p-8">
              <div className="w-10 h-10 rounded-md bg-brand-greenPale text-brand-green
                              flex items-center justify-center font-display text-lg font-bold">
                {s.n}
              </div>
              <h3 className="mt-5 font-display text-2xl text-ink leading-tight">{s.title}</h3>
              <p className="mt-3 text-base text-slateline-600 leading-relaxed">{s.body}</p>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// 3. Who reviews your work
// ---------------------------------------------------------------------------

function WhoReviews({ navigate }) {
  return (
    <section id="who" className="py-20 sm:py-28 bg-white border-y border-slateline-200 scroll-mt-16">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-[1.1fr,1fr] gap-10 lg:gap-16 items-start">
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.18em] text-brand-green">
              Who reviews your work
            </p>
            <h2 className="mt-3 font-display text-3xl sm:text-4xl lg:text-5xl leading-[1.1] tracking-tight text-ink">
              A named human.<br />Today, that's the founder.
            </h2>
            <div className="mt-6 space-y-4 text-base sm:text-lg text-slateline-600 leading-relaxed max-w-xl">
              <p>
                Every review on Xpert is done by a person you can name. Right now
                that person is Ankit Kumar, who founded Xpert. He reviews AI-built code
                and AI-built product and strategy work himself.
              </p>
              <p>
                As demand grows, we add hand-picked specialists. If your work is
                outside what he can judge well, he says so before he starts, and
                you get a full refund.
              </p>
            </div>
            <ul className="mt-8 space-y-4 max-w-xl">
              {REVIEW_PRINCIPLES.map((p) => (
                <li key={p.title} className="flex items-start gap-3">
                  <Check className="w-5 h-5 text-brand-green flex-shrink-0 mt-0.5" />
                  <div>
                    <div className="text-base font-semibold text-ink">{p.title}</div>
                    <div className="text-sm text-slateline-600 leading-relaxed mt-0.5">{p.body}</div>
                  </div>
                </li>
              ))}
            </ul>
          </div>

          <div className="bg-canvas border border-slateline-200 rounded-xl p-6 sm:p-7">
            <div className="flex items-center gap-3">
              <BrandCheck size={40} />
              <div>
                <div className="font-display text-xl text-ink leading-tight">Ankit Kumar</div>
                <div className="text-sm text-slateline-500">Founder, Xpert</div>
              </div>
            </div>
            <div className="mt-6 text-[11px] font-bold uppercase tracking-[0.14em] text-slateline-500">
              Reviews today
            </div>
            <div className="mt-3 space-y-3">
              {REVIEW_AREAS.map(({ name, sub }) => (
                <button
                  key={name}
                  type="button"
                  onClick={() => {
                    trackIfEnabled("landing_usecase_clicked", { name });
                    navigate("/get-verified");
                  }}
                  className="w-full text-left bg-white border border-slateline-200 rounded-xl p-5
                             hover:border-slateline-300 hover:shadow-sm transition-all
                             focus:outline-none focus:ring-2 focus:ring-brand-green/30"
                >
                  <div className="flex items-center justify-between gap-3">
                    <span className="font-display text-lg text-ink leading-tight">{name}</span>
                    <ArrowRight className="w-4 h-4 text-brand-green flex-shrink-0" />
                  </div>
                  <p className="mt-1.5 text-sm text-slateline-600 leading-relaxed">{sub}</p>
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// 4. Pricing: the list tiers, read from lib/reviewPricing (mirrors backend)
// ---------------------------------------------------------------------------

function Pricing({ navigate }) {
  return (
    <section id="pricing" className="py-20 sm:py-28 scroll-mt-16">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <SectionHead
          eyebrow="Pricing"
          h2={<>One list price<br />per turnaround.</>}
          sub="Same review, same reviewer. You pick how fast you need it back. Pay per review, no subscription."
        />
        <div className="grid sm:grid-cols-3 gap-5 mt-12 max-w-4xl mx-auto">
          {REVIEW_TIERS.map((t) => (
            <article key={t.key}
                     className="bg-white border border-slateline-200 rounded-xl p-6 sm:p-7 flex flex-col">
              <div className="flex items-baseline gap-2">
                <span className="font-display text-5xl font-semibold text-ink leading-none">${t.price}</span>
                <span className="text-sm text-slateline-500">/ review</span>
              </div>
              <div className="mt-3 text-sm font-semibold text-brand-green">{t.label} turnaround</div>
              <button
                onClick={() => {
                  trackIfEnabled("landing_tier_cta_clicked", { tag: t.key, to: "/get-verified" });
                  navigate("/get-verified");
                }}
                className="mt-6 inline-flex items-center justify-center gap-2 w-full h-10 rounded-md
                           bg-white border border-slateline-300 hover:border-ink/60 text-ink
                           font-semibold text-sm tracking-wide transition-colors"
              >
                Submit for review <ArrowRight className="w-4 h-4" />
              </button>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// 5. Badge wall: live public badges only
// ---------------------------------------------------------------------------

/**
 * BadgeWall shows the most recent PUBLIC badges from
 * `/api/verifications/recent`. There is no curated fallback any more: a badge
 * is private by default and only appears here when its owner agreed to
 * publish it, so an empty feed is the normal launch state and gets a quiet
 * empty state instead of examples.
 *
 * Backend item shape:
 *   { badge_slug, category, category_label, expert_name, expert_title,
 *     verdict_summary, issued_at }
 */
function BadgeWall() {
  const [tiles, setTiles] = useState([]);
  const [status, setStatus] = useState("loading"); // loading | live | empty

  useEffect(() => {
    let cancelled = false;
    axios.get(`${API}/verifications/recent?limit=6`)
      .then((r) => {
        if (cancelled) return;
        const items = Array.isArray(r.data?.items) ? r.data.items : [];
        setTiles(items.map(adaptVerificationToTile));
        setStatus(items.length ? "live" : "empty");
      })
      .catch(() => { if (!cancelled) setStatus("empty"); });
    return () => { cancelled = true; };
  }, []);

  // Render nothing while loading so the page doesn't flash an empty state.
  if (status === "loading") return null;

  if (status === "empty") {
    return (
      <section aria-label="Published reviews" className="pb-20 sm:pb-28">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="inline-flex"><BrandCheck size={28} /></div>
          <p className="mt-4 text-sm text-slateline-600 leading-relaxed">
            Published reviews will appear here. Verdicts are private by default;
            one shows up here only when its owner chooses to publish it.
          </p>
        </div>
      </section>
    );
  }

  return (
    <section className="py-20 sm:py-28 bg-white border-y border-slateline-200">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <SectionHead
          eyebrow="Reviewed by Xpert"
          h2={<>Real work. Real verdicts.</>}
          sub="Recent reviews whose owners chose to publish them. Each one is signed by a named reviewer and timestamped."
        />
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 mt-12">
          {tiles.map((t) => (
            <a key={t.key}
               href={`/verified/${t.key}`}
               className="block bg-canvas border border-slateline-200 rounded-xl p-5
                          hover:border-slateline-300 hover:shadow-sm transition-all">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-md bg-slateline-100 text-slateline-600
                                flex items-center justify-center font-display text-sm font-bold">
                  {t.logo}
                </div>
                <div className="text-sm font-semibold text-ink truncate">{t.name}</div>
              </div>
              <p className="mt-4 text-sm text-ink/85 leading-relaxed">{t.verdict}</p>
              <p className="mt-4 pt-3 border-t border-slateline-100 text-[11px] text-slateline-500
                            flex items-center gap-1.5">
                <span className="text-brand-green font-bold">✓</span> {t.foot}
              </p>
            </a>
          ))}
        </div>
      </div>
    </section>
  );
}

/**
 * Adapt a /api/verifications/recent item to the BadgeWall tile shape.
 * Pure function, kept named for testability.
 */
function adaptVerificationToTile(v) {
  // 2-letter monogram from the category label ("Code Reviews" -> "CO"),
  // never the expert's initials, so anonymous experts aren't identifiable.
  const label = v.category_label || v.category || "?";
  const logo = (label.replace(/[^A-Za-z]/g, "").slice(0, 2) || "??").toUpperCase();
  const foot = `Reviewed · ${formatRelativeTime(v.issued_at)} · ${label}`;
  return {
    key:     v.badge_slug,
    logo,
    name:    v.expert_title ? `${v.expert_name} · ${v.expert_title}` : v.expert_name,
    verdict: v.verdict_summary || "Reviewed.",
    foot,
  };
}

/**
 * Cheap relative-time formatter, no date library. Returns "18 min ago",
 * "2h ago", "yesterday", "3d ago", "2w ago", "earlier"; "recently" on bad
 * input.
 */
function formatRelativeTime(iso) {
  if (!iso) return "recently";
  const then = new Date(iso).getTime();
  if (!Number.isFinite(then)) return "recently";
  const diffMs = Date.now() - then;
  if (diffMs < 0) return "just now";
  const min = Math.floor(diffMs / 60_000);
  if (min < 1) return "just now";
  if (min < 60) return `${min} min ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const day = Math.floor(hr / 24);
  if (day === 1) return "yesterday";
  if (day < 7) return `${day}d ago`;
  const wk = Math.floor(day / 7);
  if (wk < 5) return `${wk}w ago`;
  return "earlier";
}

// ---------------------------------------------------------------------------
// 6. Final CTA
// ---------------------------------------------------------------------------

function FinalCTA({ navigate }) {
  return (
    <section id="cta" className="relative bg-ink text-white overflow-hidden">
      <div
        aria-hidden="true"
        className="absolute -top-32 left-1/2 -translate-x-1/2 w-[900px] h-[480px] rounded-full
                   bg-brand-green/30 blur-3xl pointer-events-none"
      />
      <div className="relative max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-20 sm:py-28 text-center">
        <p className="text-xs font-bold uppercase tracking-[0.18em] text-brand-green">
          The human review layer for AI-built work
        </p>
        <h2 className="mt-4 font-display text-4xl sm:text-5xl lg:text-6xl leading-tight">
          AI built it.<br />
          <em className="italic text-brand-green">Get the green check.</em>
        </h2>
        <p className="mt-6 text-base sm:text-lg text-white/70 leading-relaxed">
          Pick a turnaround, send the work, get a written verdict from a named
          reviewer. Submit, sleep, ship.
        </p>
        <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
          <button
            onClick={() => navigate("/get-verified")}
            className="inline-flex items-center gap-2 h-12 px-6 rounded-md
                       bg-brand-green hover:bg-brand-greenHover
                       text-white font-semibold text-sm tracking-wide shadow-sm transition-colors"
          >
            Get your AI-built work reviewed <ArrowRight className="w-4 h-4" />
          </button>
          <button
            onClick={() => navigate("/become-expert")}
            className="inline-flex items-center gap-2 h-12 px-6 rounded-md
                       bg-white/10 border border-white/20 hover:bg-white/15
                       text-white font-semibold text-sm tracking-wide transition-colors"
          >
            Or be an expert
          </button>
        </div>
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Shared: section head (eyebrow + h2 + optional sub)
// ---------------------------------------------------------------------------

function SectionHead({ eyebrow, h2, sub }) {
  return (
    <div className="text-center max-w-2xl mx-auto">
      <p className="text-xs font-bold uppercase tracking-[0.18em] text-brand-green">
        {eyebrow}
      </p>
      <h2 className="mt-3 font-display text-3xl sm:text-4xl lg:text-5xl
                     leading-[1.1] tracking-tight text-ink">
        {h2}
      </h2>
      {sub && (
        <p className="mt-5 text-base sm:text-lg text-slateline-600 leading-relaxed">
          {sub}
        </p>
      )}
    </div>
  );
}
